/**
 * SanPlumb — Geometría de entorno (piso, muros, planos de referencia NPT)
 *
 * Origen de migración: legacy `sanplumb-mvp__1_.html` líneas 739-791.
 *
 * Cambios respecto al MVP:
 *   - mkWalls ahora recibe `murW` desde Room (antes hardcoded en t=.10)
 *     y representa el espesor real del muro como líneas paralelas
 *   - mkHPlane recibe el color como hex number (antes string)
 *   - mkFloor sin cambios funcionales
 */

import * as THREE from 'three';
import { COLORS } from './colors';
import type { Room } from '../../types';

// ──────────────────────────────────────────────────────────────────
// mkFloor — Piso del cuarto + grilla
// ──────────────────────────────────────────────────────────────────

/**
 * Construye el piso del cuarto (PlaneGeometry) más una grilla de líneas
 * cada 0.5 m para referencia visual.
 *
 * Origen: x=0, z=0 (esquina). El piso se extiende a (w, 0, d).
 *
 * @param w  Ancho del cuarto (eje X) en metros.
 * @param d  Profundidad del cuarto (eje Z) en metros.
 */
export function mkFloor(w: number, d: number): THREE.Group {
  const g = new THREE.Group();

  // Plano del piso (semi-transparente, oscuro)
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshBasicMaterial({ color: COLORS.floor, transparent: true, opacity: 0.6 }),
  );
  plane.rotation.x = -Math.PI / 2;
  plane.position.set(w / 2, 0, d / 2);
  g.add(plane);

  // Grilla cada 0.5 m
  const pts: number[] = [];
  const step = 0.5;
  for (let x = 0; x <= w + 0.01; x += step) {
    pts.push(x, 0, 0, x, 0, d);
  }
  for (let z = 0; z <= d + 0.01; z += step) {
    pts.push(0, 0, z, w, 0, z);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
  g.add(
    new THREE.LineSegments(
      geo,
      new THREE.LineBasicMaterial({ color: COLORS.floorGrid, transparent: true, opacity: 0.7 }),
    ),
  );

  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkWalls — Muros del cuarto + vano de puerta
// ──────────────────────────────────────────────────────────────────

/**
 * Construye las aristas del cuarto (4 verticales en esquinas + marco
 * superior + jambas y dintel del vano de puerta + líneas internas
 * representando el espesor del muro `murW`).
 *
 * El vano de puerta se ubica centrado en el muro frontal (z=0).
 *
 * @param room  Dimensiones del cuarto. Usa room.murW para representar el
 *              espesor (no hardcoded como en el MVP).
 */
export function mkWalls(room: Room): THREE.Group {
  const g = new THREE.Group();
  const { w, d, h, murW } = room;

  const matWall = new THREE.LineBasicMaterial({
    color: COLORS.wallEdge,
    transparent: true,
    opacity: 0.7,
  });
  const matWallDim = new THREE.LineBasicMaterial({
    color: COLORS.wallEdgeDim,
    transparent: true,
    opacity: 0.5,
  });
  // 4 aristas verticales (esquinas)
  ([
    [0, 0],
    [w, 0],
    [w, d],
    [0, d],
  ] as const).forEach(([x, z]) => {
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x, 0, z),
      new THREE.Vector3(x, h, z),
    ]);
    g.add(new THREE.Line(geo, matWall));
  });

  // Marco superior (techo)
  ([
    [0, 0, w, 0],
    [w, 0, w, d],
    [w, d, 0, d],
    [0, d, 0, 0],
  ] as const).forEach(([x1, z1, x2, z2]) => {
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(x1, h, z1),
      new THREE.Vector3(x2, h, z2),
    ]);
    g.add(new THREE.Line(geo, matWallDim));
  });

  // Representación del espesor del muro como líneas internas
  // (paralelas a la esquina, separadas por murW hacia el interior del cuarto)
  // Muro frontal (z=0) → línea interna a z=murW
  const inner = murW;
  ([
    // Frontal: línea interna desde (0, 0, murW) a (w, 0, murW) excluyendo el vano
    { from: new THREE.Vector3(0, 0, inner), to: new THREE.Vector3(w, 0, inner) },
    // Posterior: z = d - murW
    { from: new THREE.Vector3(0, 0, d - inner), to: new THREE.Vector3(w, 0, d - inner) },
    // Lateral izquierdo: x = murW
    { from: new THREE.Vector3(inner, 0, 0), to: new THREE.Vector3(inner, 0, d) },
    // Lateral derecho: x = w - murW
    { from: new THREE.Vector3(w - inner, 0, 0), to: new THREE.Vector3(w - inner, 0, d) },
  ]).forEach(({ from, to }) => {
    const geo = new THREE.BufferGeometry().setFromPoints([from, to]);
    g.add(new THREE.Line(geo, matWallDim));
  });

  return g;
}

// ──────────────────────────────────────────────────────────────────
// mkHPlane — Plano horizontal de referencia NPT (toggleable)
// ──────────────────────────────────────────────────────────────────

/**
 * Plano horizontal semitransparente a la altura `y` indicada — sirve como
 * referencia visual de una altura NPT clave (ej. +0.30 m alimentación AF,
 * +2.05 m brazo de ducha, etc.). Cubre toda el área del cuarto.
 *
 * @param y    Altura en metros sobre NPT ±0.00.
 * @param col  Color hex (ver COLORS).
 * @param w    Ancho del cuarto en m.
 * @param d    Profundidad del cuarto en m.
 */
export function mkHPlane(y: number, col: number, w: number, d: number): THREE.Mesh {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(w, d),
    new THREE.MeshBasicMaterial({
      color: col,
      transparent: true,
      opacity: 0.05,
      side: THREE.DoubleSide,
    }),
  );
  m.rotation.x = -Math.PI / 2;
  m.position.set(w / 2, y, d / 2);
  return m;
}
